import { MediaDisplay } from "../../../resources/modelTypes";

export type AssetSpec = {
  algorithm: "crop";
  height: number;
  key: string;
  slotIndex?: number;
  width: number;
};

/** Legacy per-theme crop dimensions; modular slots are cropped to these same boxes. */
export const assetSpecs = {
  l1: {
    themeImage: {
      algorithm: "crop" as const,
      height: 1300,
      key: "theme_image",
      width: 2400,
    },
  },
  l2: {
    brideImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "bride_image",
      width: 1000,
    },
    groomImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "groom_image",
      width: 1000,
    },
  },
  l3: {
    themeImage: {
      algorithm: "crop" as const,
      height: 2134,
      key: "theme_image",
      width: 3200,
    },
  },
};

/** Crop specs for modular media slots (legacy l1/l2/l3 dimensions). */
export const modularMediaSpecs = {
  // Square slots for the overlapping collage (1:1).
  overlapping: [
    {
      algorithm: "crop" as const,
      height: 1000,
      key: "header_slot_0",
      slotIndex: 0,
      width: 1000,
    },
    {
      algorithm: "crop" as const,
      height: 1000,
      key: "header_slot_1",
      slotIndex: 1,
      width: 1000,
    },
  ],
  // A 3:4 portrait beside a 4:3 landscape, so the two stand at equal height.
  sideBySide: [
    {
      algorithm: "crop" as const,
      height: 1333,
      key: "header_slot_0",
      slotIndex: 0,
      width: 1000,
    },
    {
      algorithm: "crop" as const,
      height: 1200,
      key: "header_slot_1",
      slotIndex: 1,
      width: 1600,
    },
  ],
  square: [
    {
      ...assetSpecs.l2.brideImage,
      key: "header_slot_0",
      slotIndex: 0,
    },
    {
      ...assetSpecs.l2.groomImage,
      key: "header_slot_1",
      slotIndex: 1,
    },
  ],
} as const;

/**
 * Height for the treatments that size to a constant rather than an aspect ratio
 * (Marquee, Slideshow), so the header never jumps. Wrap in `styleUtils.rem()`.
 */
export const FIXED_MEDIA_HEIGHT = 520;

/** Aspect ratio Landscape header photos are cropped to, and display sizes to. */
export const LANDSCAPE_MEDIA_ASPECT_RATIO =
  assetSpecs.l1.themeImage.width / assetSpecs.l1.themeImage.height;

/**
 * Aspect ratio Full Bleed header photos are cropped to — wider and shorter than
 * Landscape's, since the photo spans the viewport rather than the content column.
 */
export const FULL_BLEED_MEDIA_ASPECT_RATIO =
  assetSpecs.l3.themeImage.width / assetSpecs.l3.themeImage.height;

/** Aspect ratios of the Side by Side row's two slots: portrait left, landscape right. */
export const SIDE_BY_SIDE_PORTRAIT_ASPECT_RATIO =
  modularMediaSpecs.sideBySide[0].width /
  modularMediaSpecs.sideBySide[0].height;

export const SIDE_BY_SIDE_LANDSCAPE_ASPECT_RATIO =
  modularMediaSpecs.sideBySide[1].width /
  modularMediaSpecs.sideBySide[1].height;

/** Crop box for a modular header slot at the given position. */
export const modularSlotAssetSpec = (
  slotIndex: number,
  cropSpec: Pick<AssetSpec, "algorithm" | "height" | "width">,
): AssetSpec => ({
  ...cropSpec,
  key: `header_slot_${slotIndex}`,
  slotIndex,
});

/**
 * The crop spec a header photo at `position` needs for `mediaDisplay`. Side by
 * Side's slots differ from each other, so always resolve per position. Null
 * where the treatment doesn't crop (Marquee, Slideshow, None).
 */
export const getHeaderSlotCropSpec = (
  mediaDisplay: MediaDisplay,
  position: number,
): AssetSpec | null => {
  switch (mediaDisplay) {
    case MediaDisplay.Square:
      return modularMediaSpecs.square[position] ?? null;
    case MediaDisplay.Overlapping:
      return modularMediaSpecs.overlapping[position] ?? null;
    case MediaDisplay.FullBleed:
      return modularSlotAssetSpec(position, assetSpecs.l3.themeImage);
    case MediaDisplay.Landscape:
      return modularSlotAssetSpec(position, assetSpecs.l1.themeImage);
    case MediaDisplay.SideBySide:
      return modularMediaSpecs.sideBySide[position] ?? null;
    case MediaDisplay.Marquee:
    case MediaDisplay.Slideshow:
    case MediaDisplay.None:
    default:
      return null;
  }
};

/** Most items the layout can display, so the picker stops offering more. */
export const getHeaderMediaMaxCount = (
  mediaDisplay: MediaDisplay,
): number | undefined => {
  switch (mediaDisplay) {
    case MediaDisplay.Landscape:
    case MediaDisplay.FullBleed:
      return 1;
    case MediaDisplay.Square:
    case MediaDisplay.Overlapping:
    case MediaDisplay.SideBySide:
      return 2;
    case MediaDisplay.Marquee:
    case MediaDisplay.Slideshow:
      return 10;
    case MediaDisplay.None:
    default:
      return undefined;
  }
};
